import type {
  RecordBatchMetaResponse,
  RecordMetaResponse,
  RecordReportSummary,
} from "@/lib/types";

export type ReportAvailabilityFlags = Omit<RecordReportSummary, "latest_version">;

const EMPTY_REPORT_SUMMARY: RecordReportSummary = {
  transcript: false,
  qa: false,
  general: false,
  jtbd: false,
  jtbdMarkdown: false,
  persona: false,
  custom: false,
  latest_version: 0,
};

type RecordMetaLike =
  | Pick<RecordMetaResponse, "available_versions">
  | Pick<RecordBatchMetaResponse, "available_versions" | "report_summary">
  | null
  | undefined;

export function getRecordMetaSummary(
  meta: RecordMetaLike
): RecordReportSummary | null {
  if (!meta) {
    return null;
  }

  if ("report_summary" in meta && meta.report_summary) {
    return meta.report_summary;
  }

  const availableVersions = meta.available_versions;
  if (!availableVersions) {
    return null;
  }

  const allVersions: number[] = [];
  Object.values(availableVersions).forEach((versions) => {
    if (Array.isArray(versions)) {
      allVersions.push(...versions.filter((value): value is number => typeof value === "number"));
    }
  });

  return {
    transcript: (availableVersions.transcript || []).length > 0,
    qa: (availableVersions.qa_report || []).length > 0,
    general: (availableVersions.general || []).length > 0,
    jtbd: (availableVersions.jtbd || []).length > 0,
    jtbdMarkdown: (availableVersions.jtbd_markdown_report || []).length > 0,
    persona: (availableVersions.persona_report || []).length > 0,
    custom: (availableVersions.custom_report || []).length > 0,
    latest_version: allVersions.length > 0 ? Math.max(...allVersions) : 0,
  };
}

export function getEmptyRecordMetaSummary(): RecordReportSummary {
  return { ...EMPTY_REPORT_SUMMARY };
}

export function toReportAvailabilityFlags(
  summary: RecordReportSummary | null | undefined
): ReportAvailabilityFlags {
  const normalized = summary ?? EMPTY_REPORT_SUMMARY;

  return {
    transcript: normalized.transcript,
    qa: normalized.qa,
    general: normalized.general,
    jtbd: normalized.jtbd,
    jtbdMarkdown: normalized.jtbdMarkdown,
    persona: normalized.persona,
    custom: normalized.custom,
  };
}
